import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '@/utils/tailwind';

import { ResizingInputProps } from '../types';
import { adjustTextareaHeight, calculateTextWidth } from '../utils';

// Base styles for both input and textarea
const BASE_STYLES = 'border-none outline-none p-[2px_0] bg-transparent text-sm';

/**
 * A text input component that automatically resizes based on its content
 * Can be used as a single-line input or multi-line textarea
 */
export const ResizingInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  ResizingInputProps
>((props, ref) => {
  const {
    value,
    onChange,
    onBlur,
    onKeyDown,
    onFocus,
    readOnly = false,
    className = '',
    placeholder = '',
    multiline = false,
    minWidth = '20px',
  } = props;

  const [width, setWidth] = useState<number>(0);
  const internalInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const inputRef = (ref || internalInputRef) as React.RefObject<
    HTMLInputElement | HTMLTextAreaElement
  >;

  // Function to update textarea height and input width
  const updateDimensions = useCallback(() => {
    if (!inputRef.current) return;

    setWidth(calculateTextWidth(value || placeholder, inputRef.current));

    if (multiline && inputRef.current instanceof HTMLTextAreaElement) {
      adjustTextareaHeight(inputRef.current);
    }
  }, [inputRef, value, placeholder, multiline]);

  // Initialize dimensions on mount with useLayoutEffect to prevent flicker
  useLayoutEffect(() => {
    updateDimensions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update dimensions when value or placeholder changes
  useEffect(() => {
    updateDimensions();
  }, [value, placeholder, multiline, updateDimensions]);

  // Set up ResizeObserver to handle window/container size changes
  useEffect(() => {
    if (!multiline || !inputRef.current) return;

    // Create ResizeObserver to respond to container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    // Observe the parent element of the textarea
    const parent = inputRef.current.parentElement;
    if (parent) {
      resizeObserver.observe(parent);
    }

    // Handle window resize events
    const handleResize = () => updateDimensions();
    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [inputRef, multiline, updateDimensions]);

  /**
   * Handles input changes and updates the width
   */
  const handleInput = (
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    if (inputRef.current) {
      // Don't add newlines for single-line inputs
      const newValue = !multiline
        ? target.value.replace(/\n/g, '')
        : target.value;
      setWidth(calculateTextWidth(newValue, inputRef.current));
      onChange?.(newValue);

      // Update textarea height on input
      if (multiline && inputRef.current instanceof HTMLTextAreaElement) {
        adjustTextareaHeight(inputRef.current);
      }
    } else {
      onChange?.(target.value);
    }
  };

  /**
   * Handles keyboard events
   */
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    // Prevent Enter key from adding newlines in single-line inputs
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
    }

    // Call the provided onKeyDown handler
    onKeyDown?.(e);

    // Using requestAnimationFrame to adjust height after key processing
    if (multiline && inputRef.current instanceof HTMLTextAreaElement) {
      requestAnimationFrame(() => {
        adjustTextareaHeight(inputRef.current as HTMLTextAreaElement);
      });
    }
  };

  /**
   * Renders a textarea for multiline input
   */
  const renderTextarea = () => {
    return (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={value}
        readOnly={readOnly}
        className={cn(
          BASE_STYLES,
          className,
          'w-full overflow-hidden whitespace-pre-wrap'
        )}
        style={{
          width: '100%',
          minWidth,
          resize: 'none',
          overflow: 'hidden',
          minHeight: '1.5em', // Ensure at least one line height
        }}
        onChange={(e) => {
          handleInput(e);
        }}
        onFocus={(e) => {
          if (multiline) {
            adjustTextareaHeight(inputRef.current as HTMLTextAreaElement);
          }
          onFocus?.(e);
        }}
        onBlur={(e) => onBlur?.(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={1} // Let the auto-resize handle the actual height
      />
    );
  };

  /**
   * Renders a regular input for single-line input
   */
  const renderInput = () => {
    return (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        value={value}
        readOnly={readOnly}
        className={cn(BASE_STYLES, className)}
        style={{ width: `${width}px`, minWidth }}
        onChange={(e) => handleInput(e)}
        onFocus={onFocus}
        onBlur={(e) => onBlur?.(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
    );
  };

  return multiline ? renderTextarea() : renderInput();
});

ResizingInput.displayName = 'ResizingInput';

export default ResizingInput;
